import type {
  ExternalLink,
  GeneralLink,
  InternalLink,
  InternalLinkReference,
  SSULink,
} from '@pkg/sanity-utils/linkQueries';
import { LinkFieldType } from '@pkg/sanity-utils/linkQueries';
import { convertGeneralLinkToLinkType } from '@pkg/link/src/helpers/generalLinkToLink';
import type { LinkType } from '@pkg/link/src/types';
import { OBJECT } from '@pkg/config/sanity/schemas';
import type { AppstoreButton } from '@pkg/sanity-utils/types/button';
import type { PortableTextBlock } from '@sanity/types';

/** Resolved submenu list item (one of internal/external/ssu link shape with title) */
export interface SubmenuListItem {
  _type?: string;
  _key?: string;
  title: string;
  url?: string;
  newWindow?: boolean;
  reference?: InternalLink['reference'];
  internalAnchor?: string;
}

export interface SubmenuList {
  submenuListTitle: string;
  submenuListItems: SubmenuListItem[];
}

export const TOP_LEVEL_LINK_TYPE = 'newHeader.topLevelLink';

export interface TopLevelSubmenuEntry {
  _type?: string;
  title: string;
  submenuLists?: SubmenuList[];
  additionalInfo?: PortableTextBlock[];
}
export interface TopLevelLinkEntry {
  _type: typeof TOP_LEVEL_LINK_TYPE;
  title: string;
  linkType?: LinkFieldType;
  reference?: InternalLinkReference;
  internalAnchor?: string;
  external?: string;
  ssu?: string;
  /** Editor choice; defaults to same-tab. CustomLink forces a new window for SSU links. */
  newWindow?: boolean;
}

export type TopLevelMenuEntry = TopLevelSubmenuEntry | TopLevelLinkEntry;

function isTopLevelLinkEntry(entry: TopLevelMenuEntry): entry is TopLevelLinkEntry {
  return entry._type === TOP_LEVEL_LINK_TYPE;
}

function hasTarget(entry: TopLevelLinkEntry): entry is TopLevelLinkEntry & GeneralLink {
  switch (entry.linkType) {
    case LinkFieldType.INTERNAL:
      return !!entry.reference || !!entry.internalAnchor;
    case LinkFieldType.EXTERNAL:
      return !!entry.external;
    case LinkFieldType.SSU:
      return !!entry.ssu;
    default:
      return false;
  }
}

export type NewHeaderActionItem = (ExternalLink | InternalLink | SSULink | AppstoreButton) & { _type?: string };

interface NewHeaderActionChoice {
  internalLink?: InternalLink;
  externalLink?: ExternalLink;
  ssuLink?: SSULink;
  appstoreLink?: AppstoreButton;
  button?: { _type?: string; _key?: string; title?: string; variant?: string; link?: unknown };
}

/** Same shape as GROQ: link fields at top level (no nested "action") */
export interface NewHeaderAction extends NewHeaderActionChoice {
  _key?: string;
}

/** Resolve the chosen action from the action object (one of internal/external/ssu/appstore/button) */
export function resolveNewHeaderAction(choice: NewHeaderActionChoice | null | undefined): NewHeaderActionItem | null {
  if (!choice) {
    return null;
  }
  return choice.internalLink ?? choice.externalLink ?? choice.ssuLink ?? choice.appstoreLink ?? (choice.button as NewHeaderActionItem) ?? null;
}

export interface MainNav {
  topLevelMenuItems?: TopLevelMenuEntry[];
  menuActions?: NewHeaderAction[];
  mobileHeaderActionKey?: string;
}

export interface ResolvedTopLevelItem {
  title: string;
  submenuLists?: SubmenuList[];
  additionalInfo?: PortableTextBlock[];
  /** Non-null only for a direct-link item with a destination set. */
  directLink: LinkType | null;
  /** CustomLink reads its own `newWindow` prop for internal links, not the link's field. */
  newWindow: boolean;
}

export function resolveTopLevelMenuItems(
  nav: MainNav | null | undefined,
): ResolvedTopLevelItem[] {
  return (nav?.topLevelMenuItems ?? []).map((entry) => {
    if (!isTopLevelLinkEntry(entry)) {
      return {
        title: entry.title,
        submenuLists: entry.submenuLists,
        additionalInfo: entry.additionalInfo,
        directLink: null,
        newWindow: false,
      };
    }

    const newWindow = entry.newWindow === true;

    return {
      title: entry.title,
      directLink: hasTarget(entry)
        ? convertGeneralLinkToLinkType(entry, entry.title, undefined, newWindow)
        : null,
      newWindow,
    };
  });
}

export interface MainNavLink {
  externalLink?: ExternalLink;
  internalLink?: InternalLink;
  linkType: OBJECT.NAVIGATION_LINK_INTERNAL | OBJECT.NAVIGATION_LINK_EXTERNAL;
}
